import { request, fileRequest } from '@/api/core';

/**
 * @typedef {Object} GetFilesRequestParams
 * @property {string} type Filter response by file type
 * @property {Number} page
 * @property {Number} rowsPerPage
 */

/**
 * @typedef {Object} GetFilesPaginatedResponse
 * @property {FileTypes.MatcherFile[]} items
 * @property {{
 *   page: number;
 *   pageSize: number;
 *   rowCount: number;
 *   pageCount: number;
 * }} pagination
 */

/**
 * Fetch all matcher file uploads of the specified file type
 * @param {GetFilesRequestParams}
 * @returns {Promise<GetFilesPaginatedResponse>}
 */
export const getFiles = ({ type, page = 1, rowsPerPage = 100 }) => {
  return request('files', {
    method: 'GET',
    params: {
      page,
      rowsPerPage,
      type
    }
  });
};

/**
 * Retrieve and download Matcher files
 * @param {Object} GetFileRequestParams
 * @param {string} GetFileRequestParams.id
 * @param {string} GetFileRequestParams.mimeType the file extension type
 * @param {string} GetFileRequestParams.isExport set to true if downloading a Google Sheets file
 * @param {string} responseType
 * @returns {Promise<{ data: Blob; contentDisposition: string }>}
 */
export const getFile = ({ id, mimeType, isExport }, responseType = 'blob') => {
  return request('files/download', {
    responseType,
    method: 'GET',
    params: {
      id,
      mimeType,
      export: isExport
    }
  });
};

/**
 * Download a file generated by the Matcher API (ie. previousRun file)
 * @param {String} resultId
 * @param {typeof import('../../constants/file').FileTypesMap} type
 * @returns {Promise<{ data: Blob; contentDisposition: string }>}
 */
export const getGeneratedFile = (resultId, type) => {
  return fileRequest('files/generate-and-download', {
    responseType: 'blob',
    method: 'GET',
    params: {
      type,
      resultId
    }
  });
};

/**
 * Download files from the session associated with the task (ie. __stats.html)
 * @param {String} resultId the result id of the task
 * @param {string} taskId the task the file is associated with
 * @param {string} fileName the name of the file to be retrieved
 * @param {string} mimeType file extension
 * @returns {Promise<{ data: Blob; contentDisposition: string }>}
 */
export const getTaskSessionFile = (taskId, fileName, mimeType) => {
  return fileRequest('files/download-session-file', {
    responseType: 'blob',
    method: 'GET',
    params: {
      taskId,
      fileName,
      mimeType
    }
  });
};

/**
 * Returns file type counts
 * @returns {{
 *   uploads: number;
 *   configs: number;
 *   results: number;
 * }}
 */
export const getFilesStats = () => {
  return request('files/stats', { method: 'GET' });
};
